import { useQuery } from '@tanstack/react-query'
import {
  AlertTriangle,
  Camera,
  CheckCircle2,
  Fingerprint,
  ShieldAlert,
  ShieldCheck,
  UserCheck,
} from 'lucide-react'
import { useEffect, useState } from 'react'

import type { CustomerVerification, VerificationMethod } from '@/api/types'
import { ProblemAlert } from '@/components/error-surface'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { Spinner } from '@/components/ui/spinner'
import { describeExpiry } from '@/features/approvals/expiry'
import { cn } from '@/lib/utils'

import { useCustomerMediaBlob } from './media-image'
import { useVerifyCustomer } from './mutations'
import { customerQueryOptions } from './queries'
import { verificationStore, useHeldVerification } from './verification-store'

/**
 * "Verify the member" — the step a teller works through with the customer standing at the
 * counter, immediately before a withdrawal or a transfer posts.
 *
 * It exists because posting now checks: withdrawals and transfers refuse without a fresh
 * `CustomerVerification`, and a teller finding that out from a refused posting — after telling the
 * customer it was going through — is the failure this whole screen is built to avoid.
 *
 * What it asks is deliberately plain: look at the photo, look at the person; look at the signature
 * or thumbprint in the passbook, look at the specimen on file. There is no scanner in this build —
 * `Fingerprint` is disabled and says so — so every ordinary verification is one of the *Visual*
 * methods, which is the teller's own judgement, recorded under their name. `Override` is the
 * escape hatch for everything else: no specimen captured, no photo on file, a face mask, a hand
 * injury — anything that needs a person to decide anyway. It costs nothing extra to invoke and it
 * is not meant to: what makes it safe is that every one lands on a supervisor's review queue.
 */

const METHOD_LABELS: Record<VerificationMethod, string> = {
  FingerprintMatch: 'Fingerprint',
  PhotoVisual: 'Photo comparison',
  SignatureVisual: 'Signature comparison',
  ThumbprintVisual: 'Thumbprint comparison',
  Override: 'Override',
}

const MINIMUM_OVERRIDE_REASON = 5

export interface VerifyCustomerDialogProps {
  customerId: string
  customerName: string
  open: boolean
  onOpenChange: (open: boolean) => void
  onVerified?: (verification: CustomerVerification) => void
}

export function VerifyCustomerDialog({
  customerId,
  customerName,
  open,
  onOpenChange,
  onVerified,
}: VerifyCustomerDialogProps) {
  const customer = useQuery({ ...customerQueryOptions(customerId), enabled: open })
  const photo = useCustomerMediaBlob(customerId, 'PassportPhoto', open)
  const signature = useCustomerMediaBlob(customerId, 'Signature', open)
  const thumbprint = useCustomerMediaBlob(customerId, 'Thumbprint', open)

  const verify = useVerifyCustomer(customerId)
  const [showOverride, setShowOverride] = useState(false)
  const [overrideReason, setOverrideReason] = useState('')

  const preference = customer.data?.attestationPreference
  const signatureMatchesPreference = preference === 'Signature' || preference === 'Both'
  const thumbprintMatchesPreference = preference === 'Thumbprint' || preference === 'Both'

  const record = (method: VerificationMethod, reason?: string) => {
    verify.mutate(
      {
        method,
        sampleTemplateBytes: null,
        sampleFingerPosition: null,
        overrideReason: method === 'Override' ? (reason ?? null) : null,
      },
      {
        onSuccess: (verification) => {
          verificationStore.hold(verification)
          onVerified?.(verification)
          onOpenChange(false)
        },
      },
    )
  }

  const close = (next: boolean) => {
    if (!next) {
      verify.reset()
      setShowOverride(false)
      setOverrideReason('')
    }
    onOpenChange(next)
  }

  const trimmedReason = overrideReason.trim()

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Verify {customerName}</DialogTitle>
          <DialogDescription>
            Compare the member at the counter with the photo below, and the signature or thumbprint
            in the passbook against the specimen below it. Confirming records your name against the
            method you used — it does not touch this customer&apos;s file.
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="space-y-4">
          {preference === 'NotCaptured' ? (
            <Alert tone="info">
              <ShieldAlert aria-hidden />
              <AlertDescription>
                No attestation preference is recorded for this customer, so nothing below is
                emphasised over anything else. Any specimen that has actually been captured can
                still be compared and confirmed.
              </AlertDescription>
            </Alert>
          ) : null}

          <div className="grid gap-3 sm:grid-cols-3">
            <SpecimenCard
              title="Passport photograph"
              instruction="Compare the person in front of you with this photo."
              state={photo}
              method="PhotoVisual"
              emphasised
              onConfirm={() => record('PhotoVisual')}
              pending={verify.isPending}
              customerId={customerId}
            />
            <SpecimenCard
              title="Signature specimen"
              instruction="Compare the signature just given against this specimen."
              state={signature}
              method="SignatureVisual"
              emphasised={signatureMatchesPreference}
              onConfirm={() => record('SignatureVisual')}
              pending={verify.isPending}
              customerId={customerId}
            />
            <SpecimenCard
              title="Thumbprint specimen"
              instruction="Compare the thumbprint just given against this specimen."
              state={thumbprint}
              method="ThumbprintVisual"
              emphasised={thumbprintMatchesPreference}
              onConfirm={() => record('ThumbprintVisual')}
              pending={verify.isPending}
              customerId={customerId}
            />
          </div>

          <div className="flex flex-wrap items-center gap-2 rounded-card border border-border-subtle bg-surface-muted p-3">
            <Fingerprint className="size-4 text-muted" aria-hidden />
            <span className="text-xs text-muted">
              Fingerprint matching — scanner not yet procured.
            </span>
            <Badge tone="neutral" className="ml-auto">
              Unavailable
            </Badge>
          </div>

          {!showOverride ? (
            <Button variant="ghost" size="sm" onClick={() => setShowOverride(true)}>
              <ShieldAlert />
              None of these — record an override instead
            </Button>
          ) : (
            <div className="space-y-2 rounded-card border border-warning-border bg-warning-subtle p-3">
              <Alert tone="warning" className="border-0 bg-transparent p-0">
                <ShieldAlert aria-hidden />
                <div className="space-y-1">
                  <AlertTitle>This lands on a supervisor&apos;s review queue</AlertTitle>
                  <AlertDescription>
                    Use it when nothing above lets you confirm the member honestly — no specimen
                    captured, no photo on file, or anything else that needs a judgement call. It
                    costs nothing to use and nothing is hidden: every override is reviewed by
                    whoever holds <span className="font-mono text-2xs">teller.verification.review</span>.
                  </AlertDescription>
                </div>
              </Alert>

              <div className="space-y-1.5">
                <Label htmlFor="verify-override-reason">Reason (required)</Label>
                <textarea
                  id="verify-override-reason"
                  value={overrideReason}
                  rows={2}
                  maxLength={1000}
                  onChange={(event) => setOverrideReason(event.target.value)}
                  placeholder="Why the usual methods could not be used."
                  className={cn(
                    'w-full rounded-control border border-border-subtle bg-surface px-3 py-2 text-sm text-heading transition-colors',
                    'placeholder:text-muted hover:border-border-strong focus:border-brand-text focus:outline-none',
                  )}
                />
              </div>

              <Button
                variant="danger"
                size="sm"
                disabled={trimmedReason.length < MINIMUM_OVERRIDE_REASON || verify.isPending}
                onClick={() => record('Override', trimmedReason)}
              >
                {verify.isPending ? <Spinner /> : <ShieldAlert />}
                Record the override
              </Button>
            </div>
          )}

          {verify.isError ? <ProblemAlert error={verify.error} /> : null}
        </DialogBody>

        <DialogFooter>
          <Button variant="ghost" onClick={() => close(false)} disabled={verify.isPending}>
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function SpecimenCard({
  title,
  instruction,
  state,
  method,
  onConfirm,
  pending,
  customerId,
  emphasised,
}: {
  title: string
  instruction: string
  state: ReturnType<typeof useCustomerMediaBlob>
  method: VerificationMethod
  onConfirm: () => void
  pending: boolean
  customerId: string
  emphasised?: boolean
}) {
  return (
    <div
      className={cn(
        'flex flex-col gap-2 rounded-card border p-3',
        emphasised ? 'border-brand-fill/40 bg-brand-fill-subtle' : 'border-border-subtle bg-surface',
      )}
    >
      <p className="text-xs font-semibold text-heading">{title}</p>

      <div className="flex aspect-4/3 items-center justify-center overflow-hidden rounded-control border border-border-subtle bg-surface-muted">
        {state.isPending ? (
          <Skeleton className="h-full w-full" />
        ) : state.notCaptured ? (
          <NoSpecimen customerId={customerId} />
        ) : state.error ? (
          <span className="p-3 text-center text-2xs text-danger-text">Could not be loaded.</span>
        ) : state.url ? (
          <img src={state.url} alt={title} className="h-full w-full object-contain" />
        ) : null}
      </div>

      <p className="text-2xs text-muted">{instruction}</p>

      <Button
        variant="primary"
        size="sm"
        disabled={!state.url || pending}
        onClick={onConfirm}
        aria-label={`Confirm via ${METHOD_LABELS[method]}`}
      >
        {pending ? <Spinner /> : <CheckCircle2 />}
        This matches — confirm
      </Button>
    </div>
  )
}

function NoSpecimen({ customerId }: { customerId: string }) {
  return (
    <div className="flex flex-col items-center gap-1.5 p-3 text-center">
      <Camera className="size-5 text-muted" aria-hidden />
      <p className="text-2xs text-muted">
        Not captured. Capture it from this customer&apos;s{' '}
        <a
          href={`/customers/${encodeURIComponent(customerId)}?tab=media`}
          target="_blank"
          rel="noreferrer"
          className="font-medium text-brand-text hover:text-brand-text-hover"
        >
          Media tab
        </a>
        .
      </p>
    </div>
  )
}

/**
 * The compact status strip a posting form shows above its fields — held, expiring, or absent — and
 * the button that opens the dialog.
 *
 * `required` changes the tone but not the mechanism: a withdrawal or a transfer cannot post without
 * a live verification, so an absent one reads as a warning demanding action; a deposit's is offered
 * the same way but never blocks the submit button.
 */
export function VerificationGate({
  customerId,
  customerName,
  required,
}: {
  customerId: string
  customerName: string
  required: boolean
}) {
  const held = useHeldVerification(customerId)
  const [open, setOpen] = useState(false)

  // Forces a re-render every fifteen seconds so the "Nm left" countdown, and the transition to
  // "lapsed", move without the operator having to do anything that would itself re-render this.
  const [, setTick] = useState(0)
  useEffect(() => {
    const id = window.setInterval(() => setTick((n) => n + 1), 15_000)
    return () => window.clearInterval(id)
  }, [])

  const expiry = held ? describeExpiry(held.expiresAtUtc) : null

  return (
    <>
      <div
        className={cn(
          'flex flex-wrap items-center gap-3 rounded-card border p-3',
          held
            ? 'border-success-border bg-success-subtle'
            : required
              ? 'border-warning-border bg-warning-subtle'
              : 'border-border-subtle bg-surface',
        )}
      >
        {held ? <ShieldCheck className="size-4 text-success-text" aria-hidden /> : <UserCheck className="size-4 text-muted" aria-hidden />}

        <div className="min-w-0 flex-1">
          {held ? (
            <p className="text-xs text-body">
              Verified via {METHOD_LABELS[held.method]}
              {held.verifiedBy ? ` by ${held.verifiedBy}` : ''} · {expiry?.label ?? ''}
              {held.isOverride ? ' · recorded for supervisor review' : ''}
            </p>
          ) : (
            <p className="text-xs text-body">
              {required
                ? 'The member must be verified before this can post.'
                : 'Optional: verify the member before taking cash in.'}
            </p>
          )}
        </div>

        <Button variant={held ? 'ghost' : 'secondary'} size="sm" onClick={() => setOpen(true)}>
          {held ? 'Re-verify' : 'Verify the member'}
        </Button>
      </div>

      {!held && required ? (
        <Alert tone="warning" className="mt-2">
          <AlertTriangle aria-hidden />
          <AlertDescription>
            Posting is refused with <span className="font-mono text-2xs">customers.verification_required</span> without one.
          </AlertDescription>
        </Alert>
      ) : null}

      <VerifyCustomerDialog
        customerId={customerId}
        customerName={customerName}
        open={open}
        onOpenChange={setOpen}
      />
    </>
  )
}
